import {
  useFormState,
  useStateObject,
  isObjectEmptyData,
} from 'holistic-resources';
import { useContext, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'components/Button';
import DataItem from 'components/DataItem';
import Modal from 'components/Modal';
import DateField from 'components/formFields/DateField';
import SelectField from 'components/formFields/SelectField';
import TextField from 'components/formFields/TextField';
import SectionTitle from 'components/typography/SectionTitle';
import { ProfileContext } from 'pages/profilePage/ProfilePage';
import {
  showSuccessFeedbackBar,
  showErrorFeedbackBar,
} from 'reducers/ui/uiSlice';
import { updateVisitor } from 'services/ApiService';
import { MAX_DATE_TODAY, GENDER_OPTIONS } from 'shared/constants/common';
import {
  UPDATE_SUCCESS,
  UPDATE_ERROR,
} from 'shared/constants/feedbackMessages';
import {
  displayDate,
  getGenderDisplay,
  trimString,
  validateFormRequiriedFields,
} from 'shared/helpers/common';

export default function PersonalDetailsTab() {
  const dispatch = useDispatch();
  const { profile, doSetProfile } = useContext(ProfileContext);
  const { moduleConfig = {} } = useSelector((state) => state.settings);
  const { componentConfig, textConfig = {} } = useSelector((state) => state.ui);
  const [fieldsConfig, setFieldsConfig] = useState({});

  const {
    formValue: tmpPersonalDetails,
    setFormValue: setTmpPersonalDetails,
    formChangeHandler: onChange,
  } = useFormState({});

  const [open, setOpen] = useState(false);
  const { value: formErrorMessages, updateValue: setFormErrorMessages } =
    useStateObject({
      dateOfBirth: '',
    });

  const onChangeName = (e) => {
    setTmpPersonalDetails((current) => ({
      ...current,
      fullName: {
        ...current.fullName,
        [e.target.name]: e.target.value,
      },
    }));
  };

  const onCancel = () => {
    setTmpPersonalDetails(null);
    setOpen(false);
  };

  const onEdit = () => {
    const dateOfBirthGMT = new Date(profile.dateOfBirth);
    dateOfBirthGMT.setUTCHours(0, 0, 0, 0);

    setTmpPersonalDetails({
      _id: profile._id, // eslint-disable-line no-underscore-dangle
      title: profile.title,
      fullName: profile.fullName,
      dateOfBirth: profile.dateOfBirth,
      gender: profile.gender,
    });
    setOpen(true);
  };

  useEffect(() => {
    setFieldsConfig({
      title: {
        required: moduleConfig?.['Client-Client-view-direct-title-mandatory'],
        visible: moduleConfig?.['Client-Client-view-direct-title-visible'],
        value: tmpPersonalDetails?.title,
        fieldName: 'title',
        label: 'Title',
      },
      first: {
        required: moduleConfig?.['Client-Client-view-direct-first-mandatory'],
        visible: moduleConfig?.['Client-Client-view-direct-first-visible'],
        value: tmpPersonalDetails?.fullName?.first,
        fieldName: 'first',
        label: 'First name',
      },
      middle: {
        visible: componentConfig?.clientMiddleNameEnabled,
        value: tmpPersonalDetails?.fullName?.middle,
        fieldName: 'middle',
        label: 'Middle name',
      },
      last: {
        required: moduleConfig?.['Client-Client-view-direct-last-mandatory'],
        visible: moduleConfig?.['Client-Client-view-direct-last-visible'],
        value: tmpPersonalDetails?.fullName?.last,
        fieldName: 'last',
        label: 'Last name',
      },
      dateOfBirth: {
        required:
          moduleConfig?.['Client-Client-view-direct-dateOfBirth-mandatory'],
        visible:
          moduleConfig?.['Client-Client-view-direct-dateOfBirth-visible'],
        value: tmpPersonalDetails?.dateOfBirth,
        fieldName: 'dateOfBirth',
        label: 'Date of birth',
      },
      gender: {
        required: moduleConfig?.['Client-Client-view-direct-gender-mandatory'],
        visible: moduleConfig?.['Client-Client-view-direct-gender-visible'],
        value: tmpPersonalDetails?.gender,
        fieldName: 'gender',
        label: 'Gender',
      },
    });
  }, [tmpPersonalDetails, moduleConfig, componentConfig]);

  const onSave = async () => {
    const isFormValid = validateFormRequiriedFields(
      setFormErrorMessages,
      fieldsConfig,
    );

    if (!isFormValid) {
      return;
    }

    const actualFullname = tmpPersonalDetails?.fullName
      ? { ...tmpPersonalDetails?.fullName }
      : {};
    actualFullname.last = trimString(actualFullname?.last);
    tmpPersonalDetails.fullName = { ...actualFullname };

    try {
      const { data: updatedProfile } = await updateVisitor(tmpPersonalDetails);
      if (updatedProfile) {
        doSetProfile(updatedProfile);
      }
      setOpen(false);
      dispatch(showSuccessFeedbackBar(UPDATE_SUCCESS));
    } catch (error) {
      console.error(error);
      dispatch(showErrorFeedbackBar(UPDATE_ERROR));
    }
  };

  return (
    <>
      <SectionTitle label="Personal Details" />
      <div className="lg:mt-[40px] bg-white rounded-t-lg">
        <div className="flex flex-wrap p-4">
          <DataItem
            isVisible={fieldsConfig?.title?.visible}
            label="Title"
            value={profile?.title}
            labelClassName="mb-[20px]"
          />
          <DataItem
            isVisible={fieldsConfig?.first?.visible}
            label="First name"
            value={profile?.fullName?.first}
            labelClassName="mb-[20px]"
          />
          <DataItem
            isVisible={fieldsConfig?.middle?.visible}
            label="Middle Name"
            value={profile?.fullName?.middle}
            labelClassName="mb-[20px]"
          />
          <DataItem
            isVisible={fieldsConfig?.last?.visible}
            label="Last Name"
            value={profile?.fullName?.last}
            labelClassName="mb-[20px]"
          />
          <DataItem
            isVisible={fieldsConfig?.dateOfBirth?.visible}
            label="Date of birth"
            value={displayDate(profile?.dateOfBirth, '-', undefined, true)}
            labelClassName="mb-[20px]"
          />
          <DataItem
            isVisible={fieldsConfig?.gender?.visible}
            label="Gender"
            value={getGenderDisplay(profile?.gender)}
            labelClassName="mb-[20px]"
          />
        </div>
      </div>
      <div className="bg-white rounded-b-lg border-t flex justify-end">
        <Button className="my-4 mx-6" onClick={onEdit}>
          <div className="px-6">Edit</div>
        </Button>
      </div>
      <Modal
        show={open}
        onClose={() => setOpen(false)}
        header="Edit personal details"
        body={
          <>
            <div className="text-[22px] font-medium leading-normal text-gray-800">
              Personal Details
            </div>
            {textConfig?.personalDetailsEditModalText && (
              <div className="text-gray-600 mt-2 mb-4">
                {textConfig.personalDetailsEditModalText}
              </div>
            )}
            <div className="flex flex-wrap gap-x-4 mt-4 mb-8">
              <TextField
                isVisible={fieldsConfig?.title?.visible}
                isRequired={fieldsConfig?.title?.required}
                label={fieldsConfig?.title?.label}
                name={fieldsConfig?.title?.fieldName}
                value={tmpPersonalDetails?.title}
                onChange={onChange}
                setFormErrorMessages={setFormErrorMessages}
                errorMessage={formErrorMessages?.title}
              />
              <TextField
                isVisible={fieldsConfig?.first?.visible}
                isRequired={fieldsConfig?.first?.required}
                label={fieldsConfig?.first?.label}
                name={fieldsConfig?.first?.fieldName}
                value={tmpPersonalDetails?.fullName?.first}
                onChange={onChangeName}
                setFormErrorMessages={setFormErrorMessages}
                errorMessage={formErrorMessages?.first}
              />
              <TextField
                isVisible={fieldsConfig?.middle?.visible}
                label={fieldsConfig?.middle?.label}
                name={fieldsConfig?.middle?.fieldName}
                value={tmpPersonalDetails?.fullName?.middle}
                onChange={onChangeName}
                setFormErrorMessages={setFormErrorMessages}
                errorMessage={formErrorMessages?.middle}
              />
              <TextField
                isVisible={fieldsConfig?.last?.visible}
                isRequired={fieldsConfig?.last?.required}
                label={fieldsConfig?.last?.label}
                name={fieldsConfig?.last?.fieldName}
                value={tmpPersonalDetails?.fullName?.last}
                onChange={onChangeName}
                setFormErrorMessages={setFormErrorMessages}
                errorMessage={formErrorMessages?.last}
              />
              <DateField
                isVisible={fieldsConfig?.dateOfBirth?.visible}
                maxDate={MAX_DATE_TODAY}
                isRequired={fieldsConfig?.dateOfBirth?.required}
                label={fieldsConfig?.dateOfBirth?.label}
                name={fieldsConfig?.dateOfBirth?.fieldName}
                timestampValue={tmpPersonalDetails?.dateOfBirth}
                onChange={onChange}
                setFormErrorMessages={setFormErrorMessages}
                errorMessage={formErrorMessages?.dateOfBirth}
              />
              <SelectField
                isVisible={fieldsConfig?.gender?.visible}
                isRequired={fieldsConfig?.gender?.required}
                label={fieldsConfig?.gender?.label}
                name={fieldsConfig?.gender?.fieldName}
                options={GENDER_OPTIONS}
                value={tmpPersonalDetails?.gender}
                onChange={onChange}
                errorMessage={formErrorMessages?.gender}
                setFormErrorMessages={setFormErrorMessages}
              />
            </div>
          </>
        }
        footer={
          <>
            <Button
              label="Cancel"
              secondary
              className="px-12"
              onClick={onCancel}
            />
            <Button
              disabled={!isObjectEmptyData(formErrorMessages)}
              label="Save changes"
              className="px-12"
              onClick={onSave}
            />
          </>
        }
      />
    </>
  );
}
